import { Box, GlobalStyles } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import React from 'react';

import { Neutrals } from '../../../core-components/theme/color-palette/colors';
import {
  DOCK_ATTRIBUTE,
  DOCK_SCROLL_CLEARANCE,
  fillColumnSx,
  shellVar,
  shellVarsSx,
} from '../../../core-components/theme/components/Screen';
import { surfaceSx } from '../../../core-components/theme/components/SurfaceCard';
import logo from '../../../constants/Images/digitalpaanilogo.png';
import { LOGO_INLINE_SIZE, LOGO_SIZE } from '../constants/layout';

const shellSx = (theme: Theme): SystemStyleObject<Theme> => shellVarsSx(theme, { documentScrolls: true });

const pageSx = (theme: Theme): SystemStyleObject<Theme> => ({
  boxSizing: 'border-box',
  minBlockSize: '100dvh',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.paper,
  [theme.breakpoints.up('md')]: { backgroundColor: Neutrals[50] },
});

const rootSx = [shellSx, pageSx] as const;

const headerSx = (theme: Theme): SystemStyleObject<Theme> => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 3,
  paddingInlineStart: shellVar('inlineStart'),
  paddingInlineEnd: shellVar('inlineEnd'),
  paddingBlockStart: `max(env(safe-area-inset-top), ${theme.spacing(3)})`,
  paddingBlockEnd: 3,
  [theme.breakpoints.up('md')]: {
    paddingBlockStart: `max(env(safe-area-inset-top), ${theme.spacing(6)})`,
    paddingBlockEnd: theme.spacing(6),
  },
});

const mainSx = (theme: Theme): SystemStyleObject<Theme> => ({
  flex: '1 0 auto',
  display: 'flex',
  flexDirection: 'column',
  paddingInlineStart: shellVar('inlineStart'),
  paddingInlineEnd: shellVar('inlineEnd'),
  paddingBlockStart: 2,
  [theme.breakpoints.up('md')]: {
    alignItems: 'center',
    paddingBlockStart: 0,
    paddingBlockEnd: theme.spacing(8),
  },
});

const headerlessMainSx = (theme: Theme): SystemStyleObject<Theme> => ({
  paddingBlockStart: `max(env(safe-area-inset-top), ${theme.spacing(3)})`,
  [theme.breakpoints.up('md')]: {
    paddingBlockStart: `max(env(safe-area-inset-top), ${theme.spacing(6)})`,
  },
});

const logoSx = (theme: Theme): SystemStyleObject<Theme> => ({
  display: 'block',
  alignSelf: 'center',
  inlineSize: LOGO_INLINE_SIZE.phone,
  blockSize: 'auto',
  mixBlendMode: 'multiply',
  marginBlockEnd: 2,
  [theme.breakpoints.up('md')]: {
    inlineSize: LOGO_INLINE_SIZE.desktop,
    marginBlockStart: 'auto',
    marginBlockEnd: 4,
  },
});

const cardSx = (theme: Theme): SystemStyleObject<Theme> => ({
  ...fillColumnSx,
  gap: 4,
  inlineSize: '100%',
  [theme.breakpoints.up('md')]: {
    ...surfaceSx,
    flex: '0 0 auto',
    marginBlockEnd: 'auto',
    boxSizing: 'border-box',
    inlineSize: `min(100%, ${theme.breakpoints.values.sm}px)`,
    padding: theme.spacing(8),
    boxShadow: theme.customShadows.cardResting,
  },
});

const DOCK_SCROLL_ROOM = (
  <GlobalStyles
    styles={(theme: Theme) => ({
      [`html:has([${DOCK_ATTRIBUTE}])`]: { scrollPaddingBlockEnd: DOCK_SCROLL_CLEARANCE(theme) },
    })}
  />
);

const headerlessSx = [mainSx, headerlessMainSx] as const;

const LOGO = <Box component="img" src={logo} alt="DigitalPaani" width={LOGO_SIZE.width} height={LOGO_SIZE.height} sx={logoSx} />;

export interface AuthShellProps {
  toolbar?: React.ReactNode;
  children: React.ReactNode;
}

const ShellHeader: React.FC<{ readonly toolbar: React.ReactNode }> = ({ toolbar }) => (
  <Box component="header" sx={headerSx}>
    {toolbar}
  </Box>
);

export const AuthShell: React.FC<AuthShellProps> = ({ toolbar, children }) => {
  const hasToolbar = toolbar !== undefined && toolbar !== null && toolbar !== false;
  return (
    <Box sx={rootSx}>
      {DOCK_SCROLL_ROOM}
      {hasToolbar && <ShellHeader toolbar={toolbar} />}
      <Box component="main" sx={hasToolbar ? mainSx : headerlessSx}>
        {LOGO}
        <Box sx={cardSx}>{children}</Box>
      </Box>
    </Box>
  );
};
